import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { FadeUp } from "@/components/Reveal";
import { ArrowButton } from "@/components/ArrowButton";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { waLink, buildBookingWhatsAppMessage } from "@/data/site";
import { formatDateLong } from "@/pages/Agendar";

const API = process.env.REACT_APP_BACKEND_URL;

const MODALITY_LABEL = {
  online: "Online (videochamada)",
  presencial: "Presencial — Taboão da Serra, SP",
};

const PAYMENT_LABEL = {
  pix: "Pix",
  cartao: "Cartão de crédito ou débito",
  whatsapp: "A combinar pelo WhatsApp",
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-6 border-b border-raiz-900/8 py-3.5 last:border-0">
    <span className="text-xs uppercase tracking-[0.18em] text-ink/45">{label}</span>
    <span className="text-right text-sm font-medium text-ink" data-testid={testId}>{value}</span>
  </div>
);

const Confirmacao = () => {
  const { protocol } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/journey/bookings/${protocol}`)
      .then((res) => setBooking(res.data))
      .catch(() => setError(true));
  }, [protocol]);

  if (error) {
    return (
      <section className="container-x flex min-h-[70vh] flex-col items-start justify-center pt-28" data-testid="confirmacao-error">
        <p className="eyebrow">Confirmação</p>
        <h1 className="mt-4 font-serif text-4xl font-light text-ink sm:text-5xl">Agendamento não encontrado</h1>
        <p className="mt-5 max-w-md text-base text-ink/70">
          Não localizamos este código. Se você acabou de agendar, aguarde alguns instantes ou fale comigo pelo WhatsApp.
        </p>
        <div className="mt-8">
          <ArrowButton to="/" testId="confirmacao-error-home">Voltar ao início</ArrowButton>
        </div>
      </section>
    );
  }

  if (!booking) {
    return (
      <section className="container-x pt-28" data-testid="confirmacao-loading">
        <div className="mx-auto mt-16 h-72 max-w-2xl animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" />
      </section>
    );
  }

  const firstName = booking.name.split(/\s+/)[0];
  const paid = booking.payment?.status === "approved";
  const isConfirmed = booking.status === "confirmada" || paid;
  const isInactive = ["cancelada", "expirado"].includes(booking.status);
  const whatsMsg = isConfirmed
    ? `Olá, Sabrina! Sobre o meu atendimento confirmado (protocolo ${booking.protocol}).`
    : buildBookingWhatsAppMessage(booking);

  return (
    <>
      <SEO
        title={paid ? "Pagamento confirmado | Sabrina Mota — Psicóloga" : "Pré-agendamento recebido | Sabrina Mota — Psicóloga"}
        description={paid ? "Seu pagamento foi confirmado e seu horário está garantido." : "Seu horário está pré-reservado — a confirmação acontece pelo WhatsApp com a Sabrina."}
        path={`/confirmacao/${booking.protocol}`}
      />
      <section className="pt-28 lg:pt-36" data-testid="confirmacao-page">
        <div className="container-x max-w-3xl pb-24">
          <FadeUp className="text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold-400/60 bg-gold-100/50">
              <CheckCircle2 aria-hidden="true" className="h-8 w-8 text-gold-700" />
            </span>
            <p className="eyebrow mt-8 justify-center" data-testid="confirmacao-eyebrow">
              {isConfirmed ? "Atendimento confirmado" : isInactive ? "Agendamento" : "Pré-agendamento recebido"}
            </p>
            <h1 className="mt-5 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl" data-testid="confirmacao-title">
              {isConfirmed ? (
                <>Tudo certo! 🌿</>
              ) : isInactive ? (
                "Este agendamento não está mais ativo"
              ) : (
                <>Tudo certo, <em className="italic text-raiz-700">{firstName}.</em></>
              )}
            </h1>
            <p className="lead mx-auto mt-6 max-w-xl">
              {isConfirmed
                ? `Seu atendimento foi confirmado. Enviamos os detalhes, as instruções e a opção de adicionar à agenda para o seu e-mail (${booking.email}).`
                : isInactive
                  ? "Se precisar, fale com a Sabrina pelo WhatsApp para remarcar ou tirar dúvidas."
                  : "Seu horário está pré-reservado. Para concluir, continue pelo WhatsApp — é por lá que a Sabrina confirma a disponibilidade e combina o pagamento com você."}
            </p>
          </FadeUp>

          <FadeUp delay={0.12} className="mt-10">
            <div className="rounded-3xl border border-gold-400/50 bg-gold-100/40 p-6 text-center" data-testid="confirmacao-protocolo-card">
              <p className="text-xs uppercase tracking-[0.28em] text-ink/50">Código do seu agendamento</p>
              <p className="mt-2 font-serif text-3xl tracking-wide text-gold-700" data-testid="confirmacao-protocolo">
                {booking.protocol}
              </p>
            </div>
          </FadeUp>

          <FadeUp delay={0.18} className="mt-8">
            <div className="rounded-3xl border border-raiz-900/10 bg-cream p-7 sm:p-9" data-testid="confirmacao-summary">
              <p className="text-xs uppercase tracking-[0.24em] text-raiz-600">Resumo do atendimento</p>
              <div className="mt-4">
                <Row label="Serviço" value={booking.service_title} testId="confirmacao-servico" />
                <Row label="Modalidade" value={MODALITY_LABEL[booking.modality]} testId="confirmacao-modalidade" />
                <Row label="Data" value={formatDateLong(booking.date)} testId="confirmacao-data" />
                <Row label="Horário" value={booking.time} testId="confirmacao-horario" />
                <Row label="Pagamento" value={paid ? `${booking.payment?.method === "pix" ? "Pix" : PAYMENT_LABEL[booking.payment_preference]} — pago${booking.payment?.paid_at ? ` em ${new Date(booking.payment.paid_at).toLocaleDateString("pt-BR")}` : ""}` : "Valor e orientações pelo WhatsApp"} testId="confirmacao-pagamento" />
              </div>
            </div>
          </FadeUp>

          {booking.notes && (
            <FadeUp delay={0.22} className="mt-6">
              <p className="rounded-2xl border border-raiz-900/10 bg-paper p-5 text-sm leading-relaxed text-ink/65" data-testid="confirmacao-notes">
                <span className="font-medium text-ink">Sua observação: </span>
                {booking.notes}
              </p>
            </FadeUp>
          )}

          {booking.sessions > 1 && (
            <FadeUp delay={0.24} className="mt-6">
              <p className="rounded-2xl border border-gold-400/40 bg-gold-100/40 p-5 text-sm leading-relaxed text-ink/75" data-testid="confirmacao-pacote-note">
                Este é um pacote de {booking.sessions} sessões — esta é a primeira. Após a confirmação, Sabrina compartilhará o código do pacote para você agendar as próximas sessões pelo site.
              </p>
            </FadeUp>
          )}

          {!isConfirmed && !isInactive && (
            <FadeUp delay={0.24} className="mt-6">
              <p className="rounded-2xl border border-gold-400/40 bg-gold-100/40 p-5 text-sm leading-relaxed text-ink/75" data-testid="confirmacao-cartao-note">
                <strong className="text-ink">Falta pouco. </strong>
                O WhatsApp já deve ter aberto com a sua mensagem pronta — é só enviar. Se não abriu, use o botão abaixo. Somente após a confirmação da Sabrina o seu horário fica garantido.
              </p>
            </FadeUp>
          )}

          <FadeUp delay={0.26} className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            {isConfirmed && (
              <ArrowButton
                href={`${API}/api/journey/bookings/${booking.protocol}/calendar.ics`}
                target="_blank"
                rel="noopener noreferrer"
                testId="confirmacao-calendar-button"
              >
                Adicionar à minha agenda
              </ArrowButton>
            )}
            <ArrowButton
              href={waLink(whatsMsg)}
              target="_blank"
              rel="noopener noreferrer"
              testId={isConfirmed ? "confirmacao-falar-button" : "confirmacao-whatsapp-button"}
            >
              {isConfirmed ? "Falar com Sabrina" : !paid ? "Continuar pelo WhatsApp" : "Conversar no WhatsApp"}
            </ArrowButton>
            <ArrowButton to="/" variant="outline" testId="confirmacao-home-button">
              Voltar ao início
            </ArrowButton>
          </FadeUp>

          <FadeUp delay={0.3} className="mt-8 text-center">
            <p className="text-xs text-ink/50">
              Já tem um pacote ativo?{" "}
              <Link to="/pacote" data-testid="confirmacao-pacote-link" className="text-raiz-800 underline underline-offset-4">
                Agende a próxima sessão com o código do pacote
              </Link>
            </p>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Confirmacao;
